/**
 * VoiceInterface - Handles voice call UI and functionality
 * Part of the unified TTPChatWidget
 * Optimized for Wix iframe embedding
 */

import { AgentSDK } from '../index.js';
import AudioRecorder from '../core/AudioRecorder.js';
import { checkPermissionState, getDeviceInfo, shouldUseMobileVoiceCallUI } from '../shared/MicPermissionUtils.js';
import { applyDelay } from '../shared/applyDelay.js';
import { showBlockedModal, showNoMicrophoneModal, showDisclaimerModal } from '../shared/MicPermissionModals.js';
import { Templates } from './voice/templates.js';
import { Styles } from './voice/styles.js';
import { Transcript } from './voice/transcript.js';
import { TextHandlers } from './voice/textHandlers.js';
import { Handlers } from './voice/handlers.js';
import { Desktop } from './voice/desktop.js';
import { Mobile } from './voice/mobile.js';
import { configPrefersRtlText } from './voice/rtlText.js';
export class VoiceInterface {
  constructor(config) {
    this.config = config;
    this.audioStream = null;
    this.callStartTime = null;
    this.callTimerInterval = null;
    this.sdk = null;
    this.isActive = false;
    this.isShowingDomainError = false; // Flag to track if domain error is being displayed
    // Shadow root reference for DOM queries
    this.shadowRoot = config.shadowRoot || document;
    // Text SDK reference for sending text messages during voice calls
    this.textSDK = config.textSDK || null;
    // Mobile voice UI: native iOS/Android, or narrow + touch (WebViews with desktop UA)
    this.deviceInfo = getDeviceInfo();
    const explicitMobileVoice =
      this.config.mobileVoiceUI !== undefined
        ? this.config.mobileVoiceUI
        : this.config.behavior?.mobileVoiceUI;
    this.isMobile =
      explicitMobileVoice === true
        ? true
        : explicitMobileVoice === false
          ? false
          : shouldUseMobileVoiceCallUI();
    // Conversation messages for mobile UI (owned by mobile module, initialized there)
    // Desktop conversation state
    this.conversationExpanded = false;
    // Mic and speaker mute states
    this.isMicMuted = false;
    this.isSpeakerMuted = false;
    this.isPaused = false;
    
    // Initialize AgentSDK for voice connection
    // Pass through output format configuration if provided
    // Get visualAssistant from config or agentSettingsOverride (same pattern as VoiceSDK)
    const visualAssistant = config.visualAssistant || config.agentSettingsOverride?.visualAssistant;
    
    this.sdk = new AgentSDK({
      agentId: config.agentId,
      appId: config.appId,
      websocketUrl: config.websocketUrl,
      agentSettingsOverride: config.agentSettingsOverride,
      visualAssistant: visualAssistant,
      variables: config.variables || {},
      language: config.language || 'en',
      inputFormat: config.inputFormat,
      // Output format configuration (defaults to 24 kHz, 16-bit, PCM, raw)
      outputContainer: config.outputContainer,
      outputEncoding: config.outputEncoding,
      outputSampleRate: config.outputSampleRate,
      outputChannels: config.outputChannels,
      outputBitDepth: config.outputBitDepth,
      // Pass event callbacks to AgentSDK
      onConversationStart: config.onConversationStart,
      onConversationEnd: config.onConversationEnd,
      onBargeIn: config.onBargeIn,
      onAudioStartPlaying: config.onAudioStartPlaying,
      onAudioStoppedPlaying: config.onAudioStoppedPlaying,
      onSubtitleDisplay: config.onSubtitleDisplay,
      // Flavor object (sent in hello message)
      flavor: config.flavor,
      // Shared client-tool handler map from TTPChatWidget (so the same handler
      // map serves both voice and text channels).
      sharedToolHandlers: config.sharedToolHandlers
    });
    
    // Initialize modules with context (delegation pattern)
    this.templates = new Templates(this);
    this.styles = new Styles(this);
    this.transcript = new Transcript(this);
    this.textHandlers = new TextHandlers(this);
    this.handlers = new Handlers(this);
    this.desktop = new Desktop(this);
    this.mobile = new Mobile(this);
    
    // Setup SDK event handlers
    this.setupSDKEventHandlers();
    
    // Check for resume state on initialization
    this.checkAndHandleResume();
  }

  /**
   * Get the underlying VoiceSDK instance (if available)
   * @returns {VoiceSDK_v2|null} The VoiceSDK instance or null if not available
   */
  getVoiceSDK() {
    return this.sdk?.voiceSDK || null;
  }
  
  /**
   * Check for resume state and handle auto-resume if needed
   * This should be called after SDK initialization
   * @returns {Object|null} Resume info if resuming, null otherwise
   */
  checkAndHandleResume() {
    // If we already checked and stored resume info, return it
    // Use !== undefined to distinguish between "not checked" and "checked, no resume info"
    if (this._resumeInfo !== undefined) {
      console.log('🔄 [VOICE INTERFACE] Returning cached resume info:', this._resumeInfo);
      return this._resumeInfo;
    }
    
    // Check for resume state using AgentSDK
    const resumeInfo = this.sdk.checkResumeBeforeConnect();
    
    // Store the result (even if null) to prevent checking again
    this._resumeInfo = resumeInfo;
    
    if (resumeInfo) {
      console.log('🔄 [WIDGET] Auto-resuming call:', resumeInfo);
      console.log('   From:', resumeInfo.fromPage, '→ To:', resumeInfo.toPage);
      
      // Trigger resume callback if configured
      if (this.config.onResume) {
        this.config.onResume(resumeInfo);
      }
      
      return resumeInfo;
    }
    
    return null;
  }
  
  /**
   * Setup event handlers for AgentSDK
   */
  setupSDKEventHandlers() {
    // Handle transcript updates (agent speech)
    this.sdk.onTranscript = (text) => {
      console.log('📝 [VOICE INTERFACE] onTranscript called with text:', text);
      this.updateTranscript(text);
    };

    // Handle user transcript (STT interim + final text from backend)
    // Interim (isFinal=false): update snippet only, deferred to next paint frame
    // Final (isFinal=true): update snippet + add to conversation history
    this._pendingUserTranscriptFrame = null;
    this.sdk.onUserTranscript = (text, isFinal) => {
      // Defer snippet DOM update so it doesn't block other WebSocket messages (e.g. show_products)
      if (this._pendingUserTranscriptFrame) {
        cancelAnimationFrame(this._pendingUserTranscriptFrame);
      }
      this._pendingUserTranscriptFrame = requestAnimationFrame(() => {
        this._pendingUserTranscriptFrame = null;
        const userPrefix = this.getUserTranscriptPrefix();
        this._renderUserLiveTranscriptSnippet(userPrefix, text);
      });

      // History update is synchronous (small, only on final)
      if (isFinal) {
        if (this.isMobile) {
          this.addMobileMessage('user', text);
        } else {
          this.transcript.finalizeLiveMessage();
          this.addConversationMessage('user', text);
        }
      }
    };
    
    // Handle playback started - update status to "listening" when audio actually plays
    this.sdk.onPlaybackStarted = () => {
      console.log('🔊 [VOICE INTERFACE] Playback started - updating status to listening');
      if (this.isMobile) {
        this.mobile.hideMobileLoadingState?.(); // Hide loading, reveal timer
        this.mobile.startMobileTimer?.(); // Start timer on first audio frame
        this.updateMobileStatus('listening');
      } else {
        // Desktop: update status to listening
        this.updateDesktopStatus();
      }
    };
    
    // Handle agent speaking state
    this.sdk.onAgentSpeaking = (isStart) => {
      const avatar = this.shadowRoot.getElementById('voiceAvatarActive');
      if (avatar) {
        if (isStart) {
          avatar.classList.add('speaking');
          // Update mobile status
          if (this.isMobile) {
            this.updateMobileStatus('speaking');
            // Waveform should already be animating, but ensure it is
            this.startWaveformAnimation();
          } else {
            // Desktop: update status and waveform
            this.updateDesktopStatus();
            this.startDesktopWaveformAnimation();
          }
        } else {
          avatar.classList.remove('speaking');
          // Update mobile status
          if (this.isMobile) {
            this.updateMobileStatus('listening');
            // Keep waveform animating when listening
            this.startWaveformAnimation();
          } else {
            // Desktop: update status and waveform
            this.updateDesktopStatus();
            this.startDesktopWaveformAnimation();
          }
        }
      }
    };
    
    // Handle websocket disconnection - hangup the call
    this.sdk.onDisconnected = async (event) => {
      console.log('🔌 WebSocket disconnected, hanging up call...');
      
      // Check if disconnect was due to domain error/server rejection
      // Check both code 1008 and reason text for domain-related errors
      const isDomainError = event && (
        (event.code === 1008 && event.reason && 
         (event.reason.includes('Domain not whitelisted') || 
          event.reason.includes('domain') || 
          event.reason.includes('whitelist'))) ||
        (event.code === 1008 && event.reason && event.reason.includes('localhost'))
      );
      
      if (isDomainError) {
        // Domain error - log it and show error message in widget
        console.error('❌ Domain not whitelisted - WebSocket closed:', event.code, event.reason);
        // CRITICAL: Stop audio capture/VAD if it has started and show domain error
        await this.endCallOnServerRejection();
      } else if (this.isActive) {
        // Normal disconnect - end call gracefully
        // Set isActive to false first to prevent re-entry
        this.isActive = false;
        this.endVoiceCall();
      }
    };
    
    // Handle errors
    this.sdk.onError = async (error) => {
      // Check if it's a domain error FIRST (before checking server rejection)
      const isDomainError = error && (error.message === 'DOMAIN_NOT_WHITELISTED' || 
          (error.message && error.message.includes('Domain not whitelisted')));
      
      if (isDomainError) {
        // Domain error - log it and show error message in widget
        console.error('❌ Voice SDK Error: Domain not whitelisted:', error);
        // CRITICAL: Stop audio capture/VAD if it has started
        await this.endCallOnServerRejection();
        return;
      }
      
      // Handle other server rejections gracefully (don't log as error)
      if (error.isServerRejection || error.name === 'ServerRejected') {
        // Server rejection is expected - don't log as error
        // The domain error handler will show the appropriate message
        return;
      }
      
      console.error('❌ Voice SDK Error:', error);
      this.showError(error.message || error);
    };

    // Handle e-commerce actions received on a non-ecommerce widget
    this.sdk.onUnsupportedAction = (actionType) => {
      console.warn(`⚠️ Received "${actionType}" but this is not an E-Commerce widget. Use TTPEcommerceWidget instead.`);
      const msg = 'This widget does not support e-commerce features. Please use TTPEcommerceWidget to display products.';
      if (this.config.onErrorToast) {
        this.config.onErrorToast(msg, 'warning');
      }
      if (this.isMobile && this.config.onMobileError) {
        this.config.onMobileError(msg);
      }
    };

  }
  
  /**
   * Setup text inject acknowledgment handler
   * Delegates to textHandlers module
   */
  setupTextInjectAckHandler() {
    return this.textHandlers.setupTextInjectAckHandler();
  }

  /**
   * Setup pause/resume event handlers on VoiceSDK (must be called after connect)
   */
  _setupPauseResumeHandlers() {
    if (!this.sdk?.voiceSDK) return;

    this._callResumedHandler = () => {
      this.isPaused = false;
      // Remove .paused class from all pause buttons and restore pause icon
      ['desktopPauseBtn', 'desktopPauseBtnCompact'].forEach(id => {
        const btn = this.shadowRoot.getElementById(id);
        if (!btn) return;
        btn.classList.remove('paused');
        const pauseIcon = btn.querySelector('.desktop-pause-icon');
        const playIcon = btn.querySelector('.desktop-play-icon');
        if (pauseIcon) pauseIcon.style.display = 'block';
        if (playIcon) playIcon.style.display = 'none';
      });
      // Mobile pause button
      const mobileBar = document.getElementById('mobile-voice-call-bar-container');
      const mobilePauseBtn = mobileBar?.querySelector('#mobilePauseBtn');
      if (mobilePauseBtn) {
        mobilePauseBtn.classList.remove('paused');
        const mobilePauseIcon = mobilePauseBtn.querySelector('.mobile-pause-icon');
        const mobilePlayIcon = mobilePauseBtn.querySelector('.mobile-play-icon');
        if (mobilePauseIcon) mobilePauseIcon.style.cssText = 'display: block !important; visibility: visible !important; opacity: 1 !important; width: 15px !important; height: 15px !important; margin: 0 !important; padding: 0 !important;';
        if (mobilePlayIcon) mobilePlayIcon.style.cssText = 'display: none !important; visibility: hidden !important; opacity: 0 !important;';
      }
      // Restore timer dot (remove .paused class)
      this.shadowRoot.querySelectorAll('.ttp-rec-dot').forEach(dot => dot.classList.remove('paused'));
      const mobileBarDots = mobileBar?.querySelectorAll('.mobile-duration-dot');
      if (mobileBarDots) mobileBarDots.forEach(dot => dot.classList.remove('paused'));
    };

    this.sdk.voiceSDK.on('callResumed', this._callResumedHandler);
  }

  /**
   * Handle text inject acknowledgment
   * Delegates to textHandlers module
   */
  handleTextInjectAck(data) {
    return this.textHandlers.handleTextInjectAck(data);
  }

  /**
   * Send desktop text message
   * Delegates to textHandlers module
   */
  sendDesktopTextMessage() {
    return this.textHandlers.sendDesktopTextMessage();
  }

  /**
   * Send mobile text message
   * Delegates to textHandlers module
   */
  sendMobileTextMessage() {
    return this.textHandlers.sendMobileTextMessage();
  }

  /**
   * Helper function to get translated text
   */
  t(key) {
    const lang = this.config.language || 'en';
    const translations = this.config.translations?.[lang] || this.config.translations?.en || {};
    return translations[key] || key;
  }

  /**
   * Get text with fallback: direct config > translations > key
   */
  getText(directConfigKey, translationKey) {
    // First check for direct configuration (bypasses translation system)
    if (this.config[directConfigKey] !== undefined && this.config[directConfigKey] !== null) {
      return this.config[directConfigKey];
    }
    // Fall back to translation system
    return this.t(translationKey);
  }

  /** Prefix before interim/final user speech in the live transcript strip (not history bubbles). */
  getUserTranscriptPrefix() {
    return this.getText('userTranscriptPrefix', 'userTranscriptPrefix');
  }

  /** Assistant / user live transcript and bubbles: RTL for `direction: 'rtl'` or Hebrew/Arabic `language`. */
  prefersRtlText() {
    return configPrefersRtlText(this.config);
  }

  /**
   * Live user STT line: correct RTL/LTR order, visible gap after "אתה:", and sensible ? placement (utterance in dir=auto).
   */
  _renderUserLiveTranscriptSnippet(userPrefix, text) {
    const isRtl = this.prefersRtlText();
    const raw = (userPrefix !== undefined && userPrefix !== null) ? String(userPrefix) : '';
    const trimmedPrefix = raw.replace(/\s+$/, '');
    const sepChar = isRtl ? '\u00a0' : ' ';

    const mobileBar = document.getElementById('mobile-voice-call-bar-container');
    if (mobileBar) {
      const el = mobileBar.querySelector('#mobileTranscriptText');
      if (!el) return;
      el.textContent = '';
      el.setAttribute('dir', isRtl ? 'rtl' : 'ltr');
      if (trimmedPrefix) {
        const prefixSpan = document.createElement('span');
        prefixSpan.style.fontWeight = '600';
        prefixSpan.style.color = '#c4b5fd';
        prefixSpan.textContent = trimmedPrefix;
        el.appendChild(prefixSpan);
        el.appendChild(document.createTextNode(sepChar));
      }
      const utter = document.createElement('span');
      utter.setAttribute('dir', 'auto');
      utter.textContent = text;
      el.appendChild(utter);
      el.style.color = '#fde68a';
      return;
    }

    const liveTextCollapsed = this.shadowRoot.getElementById('liveTextCollapsed');
    if (!liveTextCollapsed) return;
    liveTextCollapsed.textContent = '';
    liveTextCollapsed.classList.remove('muted');
    liveTextCollapsed.style.color = '#e8e4f0';
    liveTextCollapsed.setAttribute('dir', isRtl ? 'rtl' : 'ltr');

    if (trimmedPrefix) {
      const prefixSpan = document.createElement('span');
      prefixSpan.className = 'name ttp-user-live-prefix';
      prefixSpan.textContent = trimmedPrefix;
      liveTextCollapsed.appendChild(prefixSpan);
      liveTextCollapsed.appendChild(document.createTextNode(sepChar));
    }
    const utterance = document.createElement('span');
    utterance.className = 'ttp-user-live-utterance';
    utterance.setAttribute('dir', 'auto');
    utterance.textContent = text;
    liveTextCollapsed.appendChild(utterance);
  }

  getTooltip(key) {
    const tooltip = this.config.tooltips?.[key];
    if (tooltip !== null && tooltip !== undefined) return tooltip;
    // Use translations for default tooltips
    const defaults = {
      mute: this.t('mute'),
      speaker: this.t('speaker'),
      endCall: this.t('endCall')
    };
    return defaults[key] || '';
  }

  /**
   * Root for widget DOM queries. When useShadowDOM is false, `shadowRoot` is `document` —
   * getElementById would return the first matching id on the entire page (wrong node → static timer).
   */
  _widgetQueryRoot() {
    if (this.shadowRoot && this.shadowRoot !== document) {
      return this.shadowRoot;
    }
    return document.getElementById('ttp-widget-container');
  }

  _queryInWidget(selector) {
    const root = this._widgetQueryRoot();
    if (!root) return null;
    return root.querySelector(selector);
  }

  /**
   * Update all timer elements with the given time text
   * Always updates both desktop and compact timers to ensure consistency
   */
  _updateTimerElements(timerText, elapsed) {
    // Update legacy desktop timer if it exists
    const timerEl = this._queryInWidget('#voiceTimer');
    if (timerEl) {
      timerEl.textContent = timerText;
    }

    // Always update BOTH timer elements - the visibility optimization was causing bugs
    // where the collapsed view timer wasn't updating
    const desktopTimerText = this._queryInWidget('#desktopTimerText');
    const compactTimerText = this._queryInWidget('#compactTimerText');

    if (desktopTimerText) {
      desktopTimerText.textContent = timerText;
    }
    if (compactTimerText) {
      compactTimerText.textContent = timerText;
    }

    // Debug logging (only in first 2 seconds)
    if (!compactTimerText && !desktopTimerText && elapsed < 2000) {
      console.warn('⚠️ Timer elements not found - compactTimerText:', !!compactTimerText, 'desktopTimerText:', !!desktopTimerText, 'elapsed:', elapsed);
      const root = this._widgetQueryRoot();
      const allTimers = root ? root.querySelectorAll('[id*="Timer"], [id*="timer"]') : [];
      console.log('Found timer-like elements:', Array.from(allTimers).map(el => el.id));
    }

    // Update mobile duration badge (use container-scoped lookup for Shopify/embedded contexts)
    const mobileBar = document.getElementById('mobile-voice-call-bar-container');
    if (mobileBar) {
      const mobileDurationText = mobileBar.querySelector('#mobileDurationText');
      const mobileHeaderDuration = mobileBar.querySelector('#mobileHeaderDuration');
      if (mobileDurationText) mobileDurationText.textContent = timerText;
      if (mobileHeaderDuration) mobileHeaderDuration.textContent = timerText;
    }
  }

  /**
   * Generate HTML for voice interface
   * Delegates to templates module
   */
  generateHTML() {
    return this.templates.generateHTML();
  }

  /**
   * Generate mobile-specific HTML for minimized voice call UI
   * Delegates to templates module
   */
  generateMobileHTML() {
    return this.templates.generateMobileHTML();
  }

  /**
   * Generate CSS for voice interface
   * Delegates to styles module
   */
  generateCSS() {
    return this.styles.generateCSS();
  }

  /**
   * Setup event handlers for voice interface
   * Delegates to handlers module
   */
  setupEventHandlers(callbacks) {
    this.handlers.setupEventHandlers(callbacks);
  }

  /**
   * Setup mobile-specific event handlers
   * Delegates to handlers module
   */
  setupMobileEventHandlers() {
    return this.handlers.setupMobileEventHandlers();
  }

  /**
   * Adjust UI sizes based on container height
   * Delegates to desktop module
   */
  adjustSizesForContainer() {
    return this.desktop.adjustSizesForContainer();
  }

  /**
   * Start desktop waveform animation
   * Delegates to desktop module
   */
  startDesktopWaveformAnimation() {
    return this.desktop.startDesktopWaveformAnimation();
  }

  /**
   * Stop desktop waveform animation
   * Delegates to desktop module
   */
  stopDesktopWaveformAnimation() {
    return this.desktop.stopDesktopWaveformAnimation();
  }

  /**
   * Update desktop status display
   * Delegates to desktop module
   */
  updateDesktopStatus() {
    return this.desktop.updateDesktopStatus();
  }

  /**
   * Update live waveform visualizer
   * Delegates to desktop module
   */
  updateLiveWaveform(isActive) {
    return this.desktop.updateLiveWaveform(isActive);
  }

  /**
   * Show error message
   * Delegates to desktop module
   */
  showError(message) {
    return this.desktop.showError(message);
  }

  /**
   * Show domain validation error
   * Delegates to desktop module
   */
  showDomainError() {
    return this.desktop.showDomainError();
  }


  /**
   * Start voice call with enhanced permission handling
   */
  async startVoiceCall() {
    if (this.isActive) {
      console.log('⚠️ Voice call already active');
      return;
    }
    
    console.log('🎤 Starting voice call...');
    
    // Check permission state first
    const permissionState = await checkPermissionState();
    console.log(`📋 Current permission state: ${permissionState}`);
    
    // If permission is explicitly denied, show blocked modal
    if (permissionState === 'denied') {
      console.log('🚫 Permission is denied, showing blocked modal...');
      // Reset connecting state since we're not proceeding
      this.resetConnectingState();
      const deviceInfo = getDeviceInfo();
      showBlockedModal(
        () => {
          // Modal closed - user can try again
          console.log('Blocked modal closed');
        },
        () => {
          // User clicked refresh
          window.location.reload();
        }
      );
      // Throw error so caller knows call didn't start
      throw new Error('Microphone permission denied');
    }
    
    // Permission is 'granted', 'prompt', or 'unknown' — proceed with connection.
    // The unified permission/disclaimer modal will be shown after hello_ack if needed.
    await this.proceedWithVoiceCall();
  }

  /**
   * Proceed with voice call - connects WebSocket, waits for hello_ack,
   * shows unified permission/disclaimer modal if needed, then starts audio.
   */

  _getChatPanelEl() {
    return this.shadowRoot.getElementById('text-chat-panel') || document.getElementById('text-chat-panel');
  }

  /** Remove inline display lock so #text-chat-panel / .open CSS controls visibility. */
  _clearChatPanelDisplayLock(panel) {
    const el = panel || this._getChatPanelEl();
    if (el) el.style.removeProperty('display');
  }

  /**
   * Mobile: hide unified sheet during voice connect. !important beats #text-chat-panel.open when
   * isOpen / .open get out of sync (e.g. disclaimer close + microtasks).
   */
  _lockChatPanelHiddenForMobileVoice() {
    const panel = this._getChatPanelEl();
    if (!panel) return;
    panel.classList.remove('open');
    panel.style.setProperty('display', 'none', 'important');
  }

  async proceedWithVoiceCall() {
    try {
      // Clear conversation history for new calls (but preserve it for resume calls)
      const isResumeCall = this._resumeInfo !== null && this._resumeInfo !== undefined;
      if (!isResumeCall) {
        console.log('🆕 New call detected - clearing conversation history');
        this.transcript.clearConversationMessages();

        // Collapse conversation history panel for new calls
        this.conversationExpanded = false;
        const panel = this.shadowRoot.getElementById('desktopConversationPanel');
        const header = this.shadowRoot.getElementById('conversationHeader');
        const toggleText = this.shadowRoot.getElementById('conversationToggleText');
        const originalSection = this.shadowRoot.getElementById('desktopVoiceSection');
        const compactSection = this.shadowRoot.getElementById('compactVoiceSection');

        if (panel) {
          panel.classList.remove('expanded');
          panel.classList.add('collapsed');
        }
        if (header) {
          header.classList.remove('expanded');
        }
        if (toggleText) {
          toggleText.textContent = 'Show history';
        }
        if (originalSection) {
          originalSection.style.display = '';
        }
        if (compactSection) {
          compactSection.style.display = 'none';
        }
      } else {
        console.log('🔄 Resume call detected - preserving conversation history');
      }

      // Set up connecting UI — no mic permission yet, just show "connecting" state
      if (this.isMobile) {
        const idleState = this.shadowRoot.getElementById('voiceIdleState');
        if (idleState) {
          idleState.style.display = 'none';
        }

        this._lockChatPanelHiddenForMobileVoice();

        this.showMobileCallBarAwaitingBrowserMic();
        this.applyMobileBarConnectingCopy();
      } else {
        // Desktop: full in-panel active UI, except flavor.callView:minimized (bottom strip only).
        this.resetConnectingState();
        const idleState = this.shadowRoot.getElementById('voiceIdleState');
        const activeState = this.shadowRoot.getElementById('voiceActiveState');

        if (idleState) idleState.style.display = 'none';
        const stripOnly = this.config.desktopMinimizedEcommerceStripUi === true;
        if (activeState) {
          activeState.style.display = stripOnly ? 'none' : 'flex';
        }
        if (stripOnly && typeof this.config.onDesktopMinimizedStripCallStart === 'function') {
          try {
            this.config.onDesktopMinimizedStripCallStart();
          } catch (e) {
            console.warn('[VoiceInterface] onDesktopMinimizedStripCallStart:', e);
          }
        }
      }

      const voiceInterface = this.shadowRoot.getElementById('voiceInterface');

      // Adjust sizes based on container dimensions
      setTimeout(() => this.adjustSizesForContainer(), 50);

      // Prevent any scrolling when switching states
      if (voiceInterface) {
        voiceInterface.scrollTop = 0;
      }

      // Connect using agentId + appId (URL built internally by SDK)
      if (!this.config.agentId) {
        throw new Error('agentId is required for voice calls');
      }

      if (this.sdk && this.sdk.isConnected) {
        console.log('🔄 SDK already connected, disconnecting for clean retry...');
        try {
          this.sdk.disconnect();
          await new Promise(resolve => setTimeout(resolve, 100));
        } catch (disconnectError) {
          console.warn('Warning: Error disconnecting existing connection:', disconnectError);
        }
      }

      const connected = await this.sdk.connect();
      console.log('✅ Connected to WebSocket, connected:', connected);
      
      // Set up text inject ack handler using EventEmitter pattern
      this.setupTextInjectAckHandler();

      // Set up pause/resume event handlers on VoiceSDK
      this._setupPauseResumeHandlers();
      
      // Track if server rejected the connection
      let serverRejected = false;
      const originalOnError = this.sdk.onError;
      const originalOnDisconnected = this.sdk.onDisconnected;
      
      // Temporarily override error handlers to catch server rejection before starting audio
      this.sdk.onError = (error) => {
        if (error && (error.message === 'DOMAIN_NOT_WHITELISTED' || 
            (error.message && error.message.includes('Domain not whitelisted')))) {
          serverRejected = true;
          console.log('🚫 Server rejected connection before starting audio capture');
        }
        // Call original handler
        if (originalOnError) originalOnError.call(this.sdk, error);
      };
      
      this.sdk.onDisconnected = (event) => {
        // Check if disconnect was due to domain error
        if (event && event.code === 1008 && event.reason && 
            (event.reason.includes('Domain not whitelisted') || 
             event.reason.includes('domain') || 
             event.reason.includes('whitelist'))) {
          serverRejected = true;
          console.log('🚫 Server disconnected due to domain error before starting audio capture');
        }
        // Call original handler
        if (originalOnDisconnected) originalOnDisconnected.call(this.sdk, event);
      };
      
      // Wait for hello_ack (format negotiation complete)
      let attempts = 0;
      while (attempts < 150) {
        if (serverRejected) {
          console.log('🚫 Server rejected connection, aborting');
          throw new Error('DOMAIN_NOT_WHITELISTED');
        }

        if (this.sdk.isConnected) {
          const vs = this.sdk.voiceSDK;
          if (vs && vs.isConnected &&
              vs.helloAckReceived &&
              vs.websocket && vs.websocket.readyState === WebSocket.OPEN) {
            break;
          }
        }
        await new Promise(resolve => setTimeout(resolve, 100));
        attempts++;
      }

      // Validate connection after loop
      if (serverRejected) {
        this.sdk.onError = originalOnError;
        this.sdk.onDisconnected = originalOnDisconnected;
        throw new Error('DOMAIN_NOT_WHITELISTED');
      }

      if (!this.sdk.isConnected ||
          !this.sdk.voiceSDK ||
          !this.sdk.voiceSDK.isConnected ||
          !this.sdk.voiceSDK.websocket ||
          this.sdk.voiceSDK.websocket.readyState !== WebSocket.OPEN) {
        this.sdk.onError = originalOnError;
        this.sdk.onDisconnected = originalOnDisconnected;
        throw new Error('Connection lost before starting audio capture');
      }

      // Restore original handlers
      this.sdk.onError = originalOnError;
      this.sdk.onDisconnected = originalOnDisconnected;

      console.log('✅ hello_ack received, connection ready');

      // --- Disclaimer modal (after hello_ack, before greeting) ---
      const vs = this.sdk.voiceSDK;
      const hasDisclaimers = vs.disclaimersPending === true;
      /** Mobile: server starts greeting on disclaimer_ack — must not ack until after mic + audio session are ready, or greeting plays over the OS permission sheet and gets cut off when capture starts. */
      let sendDisclaimerAckAfterMobileMicReady = false;

      if (hasDisclaimers) {
        const disclaimerTexts = vs.lastDisclaimerPayload?.texts?.length
          ? vs.lastDisclaimerPayload.texts
          : ['Please confirm to continue.'];

        console.log('📋 Showing disclaimer modal before greeting...');
        await new Promise((resolve, reject) => {
          showDisclaimerModal({
            texts: disclaimerTexts,
            direction: this.config.direction || 'ltr',
            onAccept: () => {
              if (this.isMobile) {
                sendDisclaimerAckAfterMobileMicReady = true;
                resolve();
              } else {
                vs.sendDisclaimerAck(true);
                resolve();
              }
            },
            onCancel: () => {
              vs.sendDisclaimerAck(false);
              // Let the browser flush disclaimer_ack before teardown; otherwise disconnect() can
              // close the socket first and the server may never end usage / send DISCLAIMER_DECLINED.
              setTimeout(() => reject(new Error('DISCLAIMER_DECLINED')), 120);
            }
          });
        });
      }

      // Disclaimer just closed — re-lock sheet and force voice tab before getUserMedia / applyDelay
      // so the unified panel cannot flash the text chat for a few seconds.
      if (this.isMobile) {
        this._lockChatPanelHiddenForMobileVoice();
        if (typeof this.config.onShowVoice === 'function') {
          this.config.onShowVoice();
        }
        this.collapseMobileTranscript();
        this.mobile.setTranscriptRowInteractive(false);
      }

      // --- Request microphone and start listening ---
      console.log('✅ Proceeding to start listening...');

      // On mobile: get getUserMedia stream once, pass to startListening to avoid double-call
      if (this.isMobile) {
        try {
          const micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
          this._preliminaryInputStream = micStream;

          const primeSr = this.config.inputFormat?.sampleRate || this.config.sampleRate || 16000;
          await AudioRecorder.primeSharedContext(primeSr);

          // Hide floating button now that permission is granted
          const floatingButton = this.shadowRoot.getElementById('text-chat-button') || document.getElementById('text-chat-button');
          if (floatingButton) floatingButton.style.display = 'none';
          const mobileFab = this.shadowRoot.querySelector('.ttp-mobile-fab');
          if (mobileFab) mobileFab.style.display = 'none';

          // Android delay for AudioManager readiness
          const delayConfig = this.config.connectionDelay ?? { default: 0, android: 3000, ios: 0 };
          await applyDelay(delayConfig);

          if (sendDisclaimerAckAfterMobileMicReady) {
            vs.sendDisclaimerAck(true);
            sendDisclaimerAckAfterMobileMicReady = false;
          }
        } catch (error) {
          console.error('❌ Microphone permission denied:', error);
          this._stopPreliminaryInputStream();
          throw error;
        }
      }

      try {
        const streamToUse = this._preliminaryInputStream || null;
        if (streamToUse) this._preliminaryInputStream = null;
        await this.sdk.startListening(streamToUse);

        // Check connection still valid after startListening
        if (!this.sdk.isConnected ||
            !this.sdk.voiceSDK ||
            !this.sdk.voiceSDK.isConnected ||
            !this.sdk.voiceSDK.websocket ||
            this.sdk.voiceSDK.websocket.readyState !== WebSocket.OPEN) {
          if (this.sdk.voiceSDK?.isRecording) {
            try { await this.sdk.voiceSDK.stopRecording(); } catch (_) {}
          }
          const error = new Error('Connection lost - server may have rejected the call');
          error.name = 'ServerRejected';
          error.isServerRejection = true;
          throw error;
        }

        console.log('🎤 Started listening - permission granted');
        this._stopPreliminaryInputStream();

        this.isActive = true;
        this.resetMuteState();

        if (this.isMobile) {
          const existingBar = document.getElementById('mobile-voice-call-bar-container');
          if (!existingBar) {
            this.createMobileMinimizedBar();
          }
          if (this.messages.length === 0) {
            this.messages.push({ type: 'assistant', text: this.t('transcriptWillAppear') });
          }
          this.collapseMobileTranscript();
          this.mobile.setTranscriptRowInteractive(true);
          this.startWaveformAnimation();
        } else {
          this.startDesktopWaveformAnimation();
          this.desktop.startLiveWaveformInterval();
        }
      } catch (error) {
        if (error.isServerRejection || error.name === 'ServerRejected') {
          this.resetConnectingState();
          throw error;
        }

        console.error('❌ Failed to start listening:', error);
        this.resetConnectingState();
        if (this.isMobile) {
          const existingBar = document.getElementById('mobile-voice-call-bar-container');
          if (existingBar) this.removeMobileMinimizedBar();
          const idleState = this.shadowRoot.getElementById('voiceIdleState');
          if (idleState) idleState.style.display = 'flex';
          this._clearChatPanelDisplayLock();
          const floatingButton = this.shadowRoot.getElementById('text-chat-button') || document.getElementById('text-chat-button');
          if (floatingButton) floatingButton.style.display = '';
        } else if (
          this.config.desktopMinimizedEcommerceStripUi === true &&
          typeof this.config.onDesktopMinimizedStripLauncherRestore === 'function'
        ) {
          try {
            this.config.onDesktopMinimizedStripLauncherRestore();
          } catch (_) {}
        }
        throw error;
      }

      // Start timer (desktop only - mobile bar owns its own timer)
      if (!this.isMobile && !this.callStartTime) {
        this.callStartTime = Date.now();
        console.log('⏱️ Starting call timer at', new Date(this.callStartTime).toISOString());
        const updateTimer = () => {
          if (!this.callStartTime) return;
          const elapsed = Date.now() - this.callStartTime;
          const minutes = Math.floor(elapsed / 60000);
          const seconds = Math.floor((elapsed % 60000) / 1000);
          const timerText = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
          this._updateTimerElements(timerText, elapsed);
        };
        setTimeout(() => {
          updateTimer();
          this.callTimerInterval = setInterval(updateTimer, 1000);
        }, 100);
      }

      console.log('✅ Voice call started successfully');

    } catch (error) {
      const isDisclaimerDeclined = error && error.message === 'DISCLAIMER_DECLINED';

      // Handle server rejection gracefully (don't log as error)
      if (!(error.isServerRejection || error.name === 'ServerRejected') && !isDisclaimerDeclined) {
        console.error('❌ Error starting voice call:', error);
      }

      this._stopPreliminaryInputStream();

      // CRITICAL: Reset isActive flag so user can retry
      this.isActive = false;

      if (this.callTimerInterval) {
        clearInterval(this.callTimerInterval);
        this.callTimerInterval = null;
      }
      this.callStartTime = null;

      // User declined server disclaimer: treat as ended call (recording never started, so
      // AgentSDK would not fire onConversationEnd on disconnect alone).
      if (isDisclaimerDeclined && this.sdk && typeof this.sdk.onConversationEnd === 'function') {
        try {
          this.sdk.onConversationEnd();
        } catch (hookErr) {
          console.warn('onConversationEnd after disclaimer decline:', hookErr);
        }
      }

      if (this.sdk && this.sdk.isConnected) {
        try {
          this.sdk.disconnect();
        } catch (disconnectError) {
          console.warn('Error disconnecting SDK on error:', disconnectError);
        }
      }

      // User declined server disclaimer: full idle reset (not just resetConnectingState —
      // connect path shows voiceActiveState on desktop; without hiding it, call UI stays visible).
      if (isDisclaimerDeclined) {
        this.resetUIState();
        if (typeof this.config.onDisclaimerDeclined === 'function') {
          this.config.onDisclaimerDeclined();
        } else if (this.isMobile && typeof this.config.onCallEnd === 'function') {
          this.config.onCallEnd();
        }
        return;
      }

      this.resetConnectingState();

      // Handle specific error types with appropriate modals
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        // Permission denied - show blocked modal
        const deviceInfo = getDeviceInfo();
        showBlockedModal(
          () => {
            // Modal closed
            this.resetUIState();
          },
          () => {
            // User clicked refresh
            window.location.reload();
          }
        );
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        // No microphone found - show no mic modal
        showNoMicrophoneModal(() => {
          this.resetUIState();
        });
      } else if (error && (error.message === 'DOMAIN_NOT_WHITELISTED' || 
          (error.message && error.message.includes('Domain not whitelisted')))) {
        // Domain not whitelisted - end call and stop audio capture
        await this.endCallOnServerRejection();
      } else {
        // Other errors - show in transcript
        this.showError(error.message || error);
        this.resetUIState();
      }
    }
  }

  /**
   * Stop preliminary mic stream (ElevenLabs pattern).
   * Must be called after startListening succeeds, or on any error path.
   */
  _stopPreliminaryInputStream() {
    if (this._preliminaryInputStream) {
      try {
        this._preliminaryInputStream.getTracks().forEach((track) => track.stop());
      } catch (e) {
        console.warn('⚠️ Error stopping preliminary stream:', e);
      }
      this._preliminaryInputStream = null;
    }
  }

  /**
   * Reset UI state to idle
   */
  resetUIState() {
    // Reset connecting state first (if it was shown)
    this.resetConnectingState();
    
    // Mobile: Remove minimized bar and restore panel
    if (this.isMobile) {
      this.removeMobileMinimizedBar();
      this.stopWaveformAnimation();
      this.messages = [];
      
      this._clearChatPanelDisplayLock(this.shadowRoot.getElementById('text-chat-panel'));
      this._clearChatPanelDisplayLock(document.getElementById('text-chat-panel'));
    }
    
    const idleState = this.shadowRoot.getElementById('voiceIdleState');
    const activeState = this.shadowRoot.getElementById('voiceActiveState');
    if (idleState) idleState.style.display = 'flex';
    if (activeState) activeState.style.display = 'none';

    if (typeof this.config.onDesktopMinimizedStripLauncherRestore === 'function') {
      try {
        this.config.onDesktopMinimizedStripLauncherRestore();
      } catch (e) {
        console.warn('[VoiceInterface] onDesktopMinimizedStripLauncherRestore:', e);
      }
    }
  }

  /**
   * End call when server rejects (domain error, etc.)
   * Stops audio capture/VAD immediately and cleans up
   */
  async endCallOnServerRejection() {
    console.log('🚫 Server rejected call - ending call and stopping audio capture/VAD...');
    console.log('🚫 Calling endCallOnServerRejection() - will show domain error message');
    
    // CRITICAL: Ensure voice interface is visible before showing error
    // The voice interface might be hidden if we're on landing screen
    // We need to call the parent widget's showVoice() method if available
    const voiceInterfaceEl = this.shadowRoot.getElementById('voiceInterface');
    const landingScreen = this.shadowRoot.getElementById('landingScreen');
    
    if (voiceInterfaceEl) {
      voiceInterfaceEl.classList.add('active');
      console.log('✅ Made voice interface visible');
    }
    
    if (landingScreen) {
      landingScreen.classList.remove('active');
      console.log('✅ Hid landing screen');
    }
    
    // Also try to call parent widget's showVoice method if available via callback
    if (this.config.onShowVoice && typeof this.config.onShowVoice === 'function') {
      console.log('🚫 Calling onShowVoice callback to ensure voice interface is visible...');
      this.config.onShowVoice();
    }
    
    // CRITICAL: Stop audio capture/VAD immediately
    if (this.sdk) {
      try {
        // CRITICAL: Stop VAD and audio capture immediately
        // Check if AudioRecorder exists and is recording (even if flags aren't set yet)
        const audioRecorder = this.sdk.voiceSDK?.audioRecorder;
        const isRecording = this.sdk.isListening || 
                           this.sdk.voiceSDK?.isRecording || 
                           (audioRecorder && audioRecorder.isRecording) ||
                           (audioRecorder && audioRecorder.audioWorkletNode !== null);
        
        if (isRecording || audioRecorder) {
          console.log('🛑 Stopping audio capture/VAD...');
          
          // First, send stop message to AudioWorklet to stop VAD processing
          if (audioRecorder?.audioWorkletNode) {
            console.log('🛑 Sending stop message to AudioWorklet to stop VAD...');
            try {
              audioRecorder.audioWorkletNode.port.postMessage({ type: 'stop' });
              await new Promise(resolve => setTimeout(resolve, 150)); // Give VAD time to stop processing
            } catch (e) {
              console.warn('Error sending stop message to AudioWorklet:', e);
            }
          }
          
          // Then call stopRecording to stop AudioRecorder and close AudioContext
          if (this.sdk.voiceSDK && typeof this.sdk.voiceSDK.stopRecording === 'function') {
            try {
              await this.sdk.voiceSDK.stopRecording();
              console.log('✅ stopRecording() completed');
            } catch (e) {
              console.warn('Error calling stopRecording:', e);
            }
          } else if (this.sdk.stopListening) {
            // Fallback to stopListening if stopRecording not available
            try {
              this.sdk.stopListening();
              await new Promise(resolve => setTimeout(resolve, 200));
            } catch (e) {
              console.warn('Error calling stopListening:', e);
            }
          }
          
          // Also try stopping AudioRecorder directly if it exists
          if (audioRecorder && typeof audioRecorder.stop === 'function') {
            try {
              await audioRecorder.stop();
              console.log('✅ AudioRecorder.stop() completed');
            } catch (e) {
              console.warn('Error calling AudioRecorder.stop():', e);
            }
          }
          
          console.log('✅ Audio capture/VAD stopped');
        }
        
        // Force stop media stream tracks if they still exist (backup cleanup)
        if (audioRecorder?.mediaStream) {
          console.log('🛑 Force stopping media stream tracks (backup cleanup)...');
          audioRecorder.mediaStream.getTracks().forEach(track => {
            if (track.readyState !== 'ended') {
              track.stop();
              console.log('✅ Force stopped track:', track.kind);
            }
          });
        }
        
        // Also stop any stored audioStream
        if (this.audioStream) {
          console.log('🛑 Stopping stored audio stream...');
          this.audioStream.getTracks().forEach(track => {
            if (track.readyState !== 'ended') {
              track.stop();
            }
          });
          this.audioStream = null;
        }
        
        // Hangup the call
        this.sdk.hangup();
      } catch (error) {
        console.warn('Error stopping listening on server rejection:', error);
        // Force stop media streams even if stopListening fails
        try {
          if (this.sdk.voiceSDK?.audioRecorder?.mediaStream) {
            this.sdk.voiceSDK.audioRecorder.mediaStream.getTracks().forEach(track => track.stop());
          }
          if (this.audioStream) {
            this.audioStream.getTracks().forEach(track => track.stop());
            this.audioStream = null;
          }
        } catch (forceStopError) {
          console.warn('Error force-stopping media streams:', forceStopError);
        }
      }
      
      // Disconnect SDK
      if (this.sdk.isConnected) {
        try {
          this.sdk.disconnect();
        } catch (disconnectError) {
          console.warn('Error disconnecting SDK on server rejection:', disconnectError);
        }
      }
    }
    
    // Reset state (but keep currentView as 'voice' so we stay on voice interface)
    this.isActive = false;
    
    // CRITICAL: Set currentView to 'voice' to prevent returning to landing screen
    // This ensures the domain error stays visible
    if (this.config.onCallEnd && typeof this.config.onCallEnd === 'function') {
      // Notify parent that we're showing an error (but staying on voice interface)
      // The parent should NOT call showLanding() when domain error is shown
    }
    
    // Stop timer if running
    if (this.callTimerInterval) {
      clearInterval(this.callTimerInterval);
      this.callTimerInterval = null;
    }
    
    // Hide active state, show idle state first
    const activeState = this.shadowRoot.getElementById('voiceActiveState');
    const idleState = this.shadowRoot.getElementById('voiceIdleState');
    if (activeState) activeState.style.display = 'none';
    if (idleState) {
      idleState.style.display = 'flex';
      
      // CRITICAL: Set flag BEFORE resetting or showing error
      // This prevents resetConnectingState() from clearing the error
      this.isShowingDomainError = true;
      console.log('🚫 Setting isShowingDomainError flag to true');
      
      // Don't reset connecting state when showing domain error - go straight to showing error
      // resetConnectingState() would clear the error message
      
      // Show domain error message directly
      console.log('🚫 Calling showDomainError() to display domain error in widget...');
      this.showDomainError();
      console.log('🚫 showDomainError() completed, isShowingDomainError:', this.isShowingDomainError);
    }
    
    // Mobile: restore panel visibility and show error toast
    if (this.isMobile) {
      this._clearChatPanelDisplayLock(this.shadowRoot.getElementById('text-chat-panel'));
      this._clearChatPanelDisplayLock(document.getElementById('text-chat-panel'));
      
      // Restore floating button
      const floatingButton = this.shadowRoot.getElementById('text-chat-button');
      if (floatingButton) {
        floatingButton.style.display = '';
      } else {
        const fallbackButton = document.getElementById('text-chat-button');
        if (fallbackButton) {
          fallbackButton.style.display = '';
        }
      }
      const mobileFabEnd = this.shadowRoot.querySelector('.ttp-mobile-fab');
      if (mobileFabEnd) mobileFabEnd.style.display = '';
      
      // Remove mobile minimized bar if it exists
      this.removeMobileMinimizedBar();
      this.stopWaveformAnimation();

      // Show error in mobile landing overlay since the panel isn't visible
      const errorMsg = this.t('domainNotValidated') || 'Domain not whitelisted. Please contact support.';
      if (this.config.onMobileError) {
        this.config.onMobileError(errorMsg);
      } else if (this.config.onErrorToast) {
        this.config.onErrorToast(errorMsg, 'error');
      }
    }
  }
  
  /**
   * End voice call
   */
  endVoiceCall() {
    console.log('🔴 Ending voice call...');
    
    // Reset mic/speaker/pause state + UI immediately so next call never flashes stale UI
    this.isMicMuted = false;
    this.isSpeakerMuted = false;
    this.isPaused = false;

    // Reset mute button DOM state (desktop original + compact)
    ['desktopMuteBtn', 'desktopMuteBtnCompact'].forEach(id => {
      const btn = this.shadowRoot.getElementById(id);
      if (!btn) return;
      btn.classList.remove('muted');
      const micIcon = btn.querySelector('.desktop-mic-icon');
      const micOffIcon = btn.querySelector('.desktop-mic-off-icon');
      if (micIcon) micIcon.style.display = 'block';
      if (micOffIcon) micOffIcon.style.display = 'none';
    });

    // Reset pause button DOM state (desktop original + compact)
    ['desktopPauseBtn', 'desktopPauseBtnCompact'].forEach(id => {
      const btn = this.shadowRoot.getElementById(id);
      if (!btn) return;
      btn.classList.remove('paused');
      const pauseIcon = btn.querySelector('.desktop-pause-icon');
      const playIcon = btn.querySelector('.desktop-play-icon');
      if (pauseIcon) pauseIcon.style.display = 'block';
      if (playIcon) playIcon.style.display = 'none';
    });

    // Reset timer dots paused state
    this.shadowRoot.querySelectorAll('.ttp-rec-dot').forEach(dot => dot.classList.remove('paused'));

    // Reset speaker button DOM state (desktop original + compact)
    ['desktopSpeakerBtn', 'desktopSpeakerBtnCompact'].forEach(id => {
      const btn = this.shadowRoot.getElementById(id);
      if (!btn) return;
      btn.classList.remove('muted');
      const speakerIcon = btn.querySelector('.desktop-speaker-icon');
      const speakerOffIcon = btn.querySelector('.desktop-speaker-off-icon');
      if (speakerIcon) speakerIcon.style.display = 'block';
      if (speakerOffIcon) speakerOffIcon.style.display = 'none';
    });

    // Reset mobile mute/speaker (container-scoped for Shopify)
    const mobileBar = document.getElementById('mobile-voice-call-bar-container');
    const mobileMuteBtn = mobileBar?.querySelector('#mobileMuteBtn');
    if (mobileMuteBtn) {
      mobileMuteBtn.classList.remove('muted');
      const mobileMicIcon = mobileMuteBtn.querySelector('.mobile-mic-icon');
      const mobileMicOffIcon = mobileMuteBtn.querySelector('.mobile-mic-off-icon');
      if (mobileMicIcon) mobileMicIcon.style.cssText = 'display: block !important; visibility: visible !important; opacity: 1 !important; width: 15px !important; height: 15px !important; margin: 0 !important; padding: 0 !important;';
      if (mobileMicOffIcon) mobileMicOffIcon.style.cssText = 'display: none !important; visibility: hidden !important; opacity: 0 !important;';
    }
    const mobileSpeakerBtn = mobileBar?.querySelector('#mobileSpeakerBtn');
    if (mobileSpeakerBtn) {
      mobileSpeakerBtn.classList.remove('muted');
      const mobileSpeakerIcon = mobileSpeakerBtn.querySelector('.mobile-speaker-icon');
      const mobileSpeakerOffIcon = mobileSpeakerBtn.querySelector('.mobile-speaker-off-icon');
      if (mobileSpeakerIcon) mobileSpeakerIcon.style.cssText = 'display: block !important; visibility: visible !important; opacity: 1 !important; width: 15px !important; height: 15px !important; margin: 0 !important; padding: 0 !important;';
      if (mobileSpeakerOffIcon) mobileSpeakerOffIcon.style.cssText = 'display: none !important; visibility: hidden !important; opacity: 0 !important;';
    }
    const mobilePauseBtn = mobileBar?.querySelector('#mobilePauseBtn');
    if (mobilePauseBtn) {
      mobilePauseBtn.classList.remove('paused');
      const mobilePauseIcon = mobilePauseBtn.querySelector('.mobile-pause-icon');
      const mobilePlayIcon = mobilePauseBtn.querySelector('.mobile-play-icon');
      if (mobilePauseIcon) mobilePauseIcon.style.cssText = 'display: block !important; visibility: visible !important; opacity: 1 !important; width: 15px !important; height: 15px !important; margin: 0 !important; padding: 0 !important;';
      if (mobilePlayIcon) mobilePlayIcon.style.cssText = 'display: none !important; visibility: hidden !important; opacity: 0 !important;';
    }

    // Reset resume info so next call is treated as a new call (not resume)
    // Resume info is only valid for the first call after page load/refresh
    this._resumeInfo = null;
    
    // Stop listening and hangup gracefully
    if (this.sdk && this.isActive) {
      this.sdk.stopListening();
      this.sdk.hangup();
      this.isActive = false;
    }
    
    // Stop audio stream (if any)
    if (this.audioStream) {
      this.audioStream.getTracks().forEach(track => track.stop());
      this.audioStream = null;
    }
    
    // Stop timer
    if (this.callTimerInterval) {
      clearInterval(this.callTimerInterval);
      this.callTimerInterval = null;
    }
    
    // Clean up text inject timeouts and handlers (desktop)
    if (this._textInjectTimeout) {
      clearTimeout(this._textInjectTimeout);
      this._textInjectTimeout = null;
    }

    if (this._statusClearTimeout) {
      clearTimeout(this._statusClearTimeout);
      this._statusClearTimeout = null;
    }

    // Clean up text inject timeouts and handlers (mobile)
    if (this._mobileTextInjectTimeout) {
      clearTimeout(this._mobileTextInjectTimeout);
      this._mobileTextInjectTimeout = null;
    }

    if (this._mobileStatusClearTimeout) {
      clearTimeout(this._mobileStatusClearTimeout);
      this._mobileStatusClearTimeout = null;
    }

    // Remove text inject ack handler (delegated to textHandlers)
    if (this.sdk?.voiceSDK && this.textHandlers._textInjectAckHandler) {
      this.sdk.voiceSDK.off('textInjectAck', this.textHandlers._textInjectAckHandler);
      this.textHandlers._textInjectAckHandler = null;
    }

    // Remove call resumed handler
    if (this.sdk?.voiceSDK && this._callResumedHandler) {
      this.sdk.voiceSDK.off('callResumed', this._callResumedHandler);
      this._callResumedHandler = null;
    }

    // Reset hint text references
    this._originalHintText = null;
    this._originalMobileHintText = null;
    
    // Desktop-specific cleanup
    if (!this.isMobile) {
      this.stopDesktopWaveformAnimation();
      // Stop live waveform interval (delegated to desktop module)
      this.desktop.stopLiveWaveformInterval();
      this.transcript.clearConversationMessages();
      this.conversationExpanded = false;
    }
    
    // Mobile-specific cleanup
    if (this.isMobile) {
      this.stopWaveformAnimation();
      this.collapseMobileTranscript();
      this.removeMobileMinimizedBar();
      this.messages = [];
      
      // Restore mobile FAB visibility (it was hidden when call started)
      const mobileFab = this.shadowRoot.querySelector('.ttp-mobile-fab');
      if (mobileFab) mobileFab.style.display = '';
      
      // Restore widget panel (remove inline display:none)
      const panel = this.shadowRoot.getElementById('text-chat-panel');
      this._clearChatPanelDisplayLock(panel);
      const fallbackPanel = !panel ? document.getElementById('text-chat-panel') : null;
      this._clearChatPanelDisplayLock(fallbackPanel);
    }
    
    // Restore floating button visibility (it was hidden when call started)
    const floatingButton = this.shadowRoot.getElementById('text-chat-button');
    if (floatingButton) {
      floatingButton.style.display = ''; // Remove inline display:none
      console.log('✅ Floating button restored (call ended)');
    } else {
      // Fallback: try document if not in Shadow DOM
      const fallbackButton = document.getElementById('text-chat-button');
      if (fallbackButton) {
        fallbackButton.style.display = '';
        console.log('✅ Floating button restored (call ended, fallback)');
      }
    }
    
    // Hide active state, restore idle state
    const activeState = this.shadowRoot.getElementById('voiceActiveState');
    if (activeState) activeState.style.display = 'none';
    const idleState = this.shadowRoot.getElementById('voiceIdleState');
    if (idleState) idleState.style.display = 'flex';
    
    // Call the public callback if provided (for user tracking/analytics)
    if (typeof this.config.onVoiceCallButtonClick === 'function') {
      try {
        this.config.onVoiceCallButtonClick({ timestamp: Date.now(), widgetState: 'end' });
      } catch (error) {
        console.error('Error in onVoiceCallButtonClick callback:', error);
      }
    }

    // Call the internal callback if provided (for unified mode - shows landing screen)
    if (this.config.onCallEnd) {
      this.config.onCallEnd();
    }
    
    // Re-adjust sizes for idle state
    setTimeout(() => this.adjustSizesForContainer(), 50);
    
    // Reset transcript
    const transcriptEl = this.shadowRoot.getElementById('transcriptText');
    if (transcriptEl) {
      transcriptEl.textContent = this.t('transcriptWillAppear');
      transcriptEl.classList.add('empty');
    }
    
    // Reset mobile transcript (container-scoped for Shopify)
    const mobileBarTx = document.getElementById('mobile-voice-call-bar-container');
    if (mobileBarTx) {
      const mobileTranscriptText = mobileBarTx.querySelector('#mobileTranscriptText');
      if (mobileTranscriptText) {
        mobileTranscriptText.textContent = this.t('transcriptWillAppear');
      }
    }
    
    // Remove speaking state from avatar
    const avatar = this.shadowRoot.getElementById('voiceAvatarActive');
    if (avatar) {
      avatar.classList.remove('speaking');
    }
    
    console.log('✅ Voice call ended');
  }
  
  /**
   * Show connecting state (between landing screen and active call)
   * This provides user feedback during WebSocket connection and SDK initialization
   * 
   * NOTE: On mobile, this should NOT be called - the minimized bar handles connecting state instead
   */
  showConnectingState() {
    // CRITICAL: On mobile, never show the idle state with connecting message
    // The minimized bar handles the connecting state on mobile
    if (this.isMobile) {
      console.log('📱 Mobile device - skipping showConnectingState() (minimized bar handles connecting state)');
      return;
    }
    
    const idleState = this.shadowRoot.getElementById('voiceIdleState');
    const activeState = this.shadowRoot.getElementById('voiceActiveState');
    
    if (idleState) {
      // Update idle state to show connecting message
      const statusTitle = idleState.querySelector('.voice-status-title');
      const statusSubtitle = idleState.querySelector('.voice-status-subtitle');
      const startCallBtn = idleState.querySelector('#startCallBtn');
      
      if (statusTitle) {
        statusTitle.textContent = this.t('connecting');
      }
      if (statusSubtitle) {
        statusSubtitle.textContent = this.t('connectingSubtitle');
      }
      if (startCallBtn) {
        startCallBtn.disabled = true;
        startCallBtn.style.opacity = '0.6';
        startCallBtn.style.cursor = 'not-allowed';
        // Update button text to show connecting state
        const btnText = startCallBtn.querySelector('span');
        if (btnText) {
          // Create spinner element
          const spinner = document.createElement('span');
          spinner.className = 'connecting-spinner';
          // Clear existing content and add spinner + text
          btnText.textContent = '';
          btnText.appendChild(spinner);
          btnText.appendChild(document.createTextNode(' ' + this.t('connecting')));
        }
      }
      
      // Show idle state (which now displays connecting message)
      idleState.style.display = 'flex';
    }
    
    // Ensure active state is hidden
    if (activeState) {
      activeState.style.display = 'none';
    }
  }
  
  /**
   * Reset connecting state back to normal idle state
   * Called when call fails to start or is cancelled
   */
  resetConnectingState() {
    // CRITICAL: Don't reset if we're showing a domain error
    if (this.isShowingDomainError) {
      console.log('🚫 resetConnectingState() called but domain error is displayed - skipping reset');
      return;
    }
    
    const idleState = this.shadowRoot.getElementById('voiceIdleState');
    
    if (idleState) {
      // Clear domain error data attribute
      delete idleState.dataset.domainError;

      // --- Restore new desktop design elements ---
      const headline = idleState.querySelector('.ttp-headline');
      const subline = idleState.querySelector('.ttp-subline');
      const onlineDot = idleState.querySelector('.ttp-online-dot');
      const agentRole = idleState.querySelector('.ttp-agent-role');

      if (headline) {
        headline.textContent = this.config.headline || this.config.startCallTitle || 'Hi there 👋';
        headline.style.color = '';
      }
      if (subline) {
        subline.innerHTML = this.config.subline || this.config.startCallSubtitle || 'Ask me anything — I respond instantly<br>in voice or text.';
        subline.style.color = '';
      }
      if (onlineDot) onlineDot.style.background = '';
      if (agentRole) {
        agentRole.textContent = this.config.agentRole || 'AI Voice Assistant';
        agentRole.style.color = '';
      }

      // Show original buttons, remove error card
      const body = idleState.querySelector('.ttp-body');
      if (body) {
        const startCallBtn = body.querySelector('#startCallBtn');
        const startTextBtn = body.querySelector('#startTextBtn');
        if (startCallBtn) startCallBtn.style.display = '';
        if (startTextBtn) startTextBtn.style.display = '';

        const errorCard = body.querySelector('.ttp-domain-error-card');
        if (errorCard) errorCard.remove();
        const dismissBtn = body.querySelector('#domainErrorDismissBtn');
        if (dismissBtn) dismissBtn.remove();
      }

      // --- Also handle legacy mobile elements ---
      const statusTitle = idleState.querySelector('.voice-status-title');
      const statusSubtitle = idleState.querySelector('.voice-status-subtitle');
      const startCallBtn = idleState.querySelector('#startCallBtn');

      if (statusTitle) {
        statusTitle.textContent = this.getText('startCallTitle', 'clickToStartCall');
        statusTitle.style.cssText = '';
      }
      if (statusSubtitle) {
        statusSubtitle.textContent = this.getText('startCallSubtitle', 'realTimeVoice');
        statusSubtitle.style.cssText = '';
      }
      if (startCallBtn) {
        startCallBtn.disabled = false;
        startCallBtn.style.opacity = '';
        startCallBtn.style.cursor = '';
        startCallBtn.style.display = '';
      }

      // Remove old back button if it exists
      const domainErrorBackBtn = idleState.querySelector('#domainErrorBackBtn');
      if (domainErrorBackBtn?.parentNode) {
        domainErrorBackBtn.parentNode.removeChild(domainErrorBackBtn);
      }
    }
  }
  
  /**
   * Update transcript text
   * Delegates to transcript module
   */
  updateTranscript(text) {
    return this.transcript.updateTranscript(text);
  }

  /**
   * Toggle conversation panel expand/collapse
   * Delegates to transcript module
   */
  toggleConversationPanel() {
    return this.transcript.toggleConversationPanel();
  }

  /**
   * Add message to conversation history
   * Delegates to transcript module
   */
  addConversationMessage(role, content) {
    return this.transcript.addConversationMessage(role, content);
  }

  /**
   * Render conversation messages
   * Delegates to transcript module
   */
  renderConversationMessages() {
    return this.transcript.renderConversationMessages();
  }
  
  /**
   * Escape HTML to prevent XSS
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
  

  /**
   * Toggle mute state
   */
  toggleMute() {
    if (!this.sdk || !this.isActive) {
      return;
    }
    
    // Access the audio recorder's media stream through VoiceSDK
    let audioTrack = null;
    if (this.sdk.voiceSDK && this.sdk.voiceSDK.audioRecorder && this.sdk.voiceSDK.audioRecorder.mediaStream) {
      audioTrack = this.sdk.voiceSDK.audioRecorder.mediaStream.getAudioTracks()[0];
    }
    
    // Fallback to stored audioStream if available
    if (!audioTrack && this.audioStream) {
      audioTrack = this.audioStream.getAudioTracks()[0];
    }
    
    if (audioTrack) {
      audioTrack.enabled = !audioTrack.enabled;
      this.isMicMuted = !audioTrack.enabled;
      
      // Desktop mute button (both original and compact layouts)
      const muteBtn = this.shadowRoot.getElementById('desktopMuteBtn');
      const muteBtnCompact = this.shadowRoot.getElementById('desktopMuteBtnCompact');
      
      // Update desktop status
      if (!this.isMobile) {
        this.updateDesktopStatus();
      }
      
      // Update original layout button
      if (muteBtn) {
        const micIcon = muteBtn.querySelector('.desktop-mic-icon');
        const micOffIcon = muteBtn.querySelector('.desktop-mic-off-icon');
        if (micIcon && micOffIcon) {
          if (!audioTrack.enabled) {
            muteBtn.classList.add('muted');
            micIcon.style.display = 'none';
            micOffIcon.style.display = 'block';
          } else {
            muteBtn.classList.remove('muted');
            micIcon.style.display = 'block';
            micOffIcon.style.display = 'none';
          }
        }
      }
      
      // Update compact layout button (sync state)
      if (muteBtnCompact) {
        const micIconCompact = muteBtnCompact.querySelector('.desktop-mic-icon');
        const micOffIconCompact = muteBtnCompact.querySelector('.desktop-mic-off-icon');
        if (micIconCompact && micOffIconCompact) {
          if (!audioTrack.enabled) {
            muteBtnCompact.classList.add('muted');
            micIconCompact.style.display = 'none';
            micOffIconCompact.style.display = 'block';
          } else {
            muteBtnCompact.classList.remove('muted');
            micIconCompact.style.display = 'block';
            micOffIconCompact.style.display = 'none';
          }
        }
      }
      
      // Mobile mute button (container-scoped for Shopify)
      if (this.isMobile) {
        const mobileBar = document.getElementById('mobile-voice-call-bar-container');
        const mobileMuteBtn = mobileBar?.querySelector('#mobileMuteBtn');
        const mobileMicIcon = mobileMuteBtn?.querySelector('.mobile-mic-icon');
        const mobileMicOffIcon = mobileMuteBtn?.querySelector('.mobile-mic-off-icon');
        
        if (mobileMuteBtn && mobileMicIcon && mobileMicOffIcon) {
          if (!audioTrack.enabled) {
            // Muted: hide white icon, show red icon
            mobileMuteBtn.classList.add('muted');
            mobileMicIcon.style.cssText = 'display: none !important; visibility: hidden !important; opacity: 0 !important;';
            mobileMicOffIcon.style.cssText = 'display: block !important; visibility: visible !important; opacity: 1 !important; width: 15px !important; height: 15px !important; margin: 0 !important; padding: 0 !important;';
          } else {
            // Unmuted: show white icon, hide red icon
            mobileMuteBtn.classList.remove('muted');
            mobileMicIcon.style.cssText = 'display: block !important; visibility: visible !important; opacity: 1 !important; width: 15px !important; height: 15px !important; margin: 0 !important; padding: 0 !important;';
            mobileMicOffIcon.style.cssText = 'display: none !important; visibility: hidden !important; opacity: 0 !important;';
          }
        }
      }
      
      console.log(audioTrack.enabled ? '🔊 Unmuted' : '🔇 Muted');
    } else {
      console.warn('⚠️ No audio track available for mute toggle');
    }
  }

  /**
   * Toggle pause state
   */
  togglePause() {
    if (!this.sdk || !this.isActive) {
      return;
    }

    if (!this.isPaused) {
      // Pause the call
      this.isPaused = true;
      if (this.sdk.voiceSDK) {
        this.sdk.pauseCall();
      }

      // Desktop pause buttons (both original and compact layouts)
      ['desktopPauseBtn', 'desktopPauseBtnCompact'].forEach(id => {
        const btn = this.shadowRoot.getElementById(id);
        if (!btn) return;
        btn.classList.add('paused');
        const pauseIcon = btn.querySelector('.desktop-pause-icon');
        const playIcon = btn.querySelector('.desktop-play-icon');
        if (pauseIcon) pauseIcon.style.display = 'none';
        if (playIcon) playIcon.style.display = 'block';
      });

      // Mobile pause button (container-scoped for Shopify)
      if (this.isMobile) {
        const mobileBar = document.getElementById('mobile-voice-call-bar-container');
        const mobilePauseBtn = mobileBar?.querySelector('#mobilePauseBtn');
        if (mobilePauseBtn) {
          mobilePauseBtn.classList.add('paused');
          const mobilePauseIcon = mobilePauseBtn.querySelector('.mobile-pause-icon');
          const mobilePlayIcon = mobilePauseBtn.querySelector('.mobile-play-icon');
          if (mobilePauseIcon) mobilePauseIcon.style.cssText = 'display: none !important; visibility: hidden !important; opacity: 0 !important;';
          if (mobilePlayIcon) mobilePlayIcon.style.cssText = 'display: block !important; visibility: visible !important; opacity: 1 !important; width: 15px !important; height: 15px !important; margin: 0 !important; padding: 0 !important;';
        }
      }

      // Add .paused class to timer dots
      this.shadowRoot.querySelectorAll('.ttp-rec-dot').forEach(dot => dot.classList.add('paused'));
      if (this.isMobile) {
        const mobileBar = document.getElementById('mobile-voice-call-bar-container');
        mobileBar?.querySelectorAll('.mobile-duration-dot').forEach(dot => dot.classList.add('paused'));
      }

      console.log('⏸️ Call paused');
    } else {
      // Resume the call
      this.isPaused = false;
      if (this.sdk.voiceSDK) {
        this.sdk.resumeCall();
      }

      // Restore desktop pause buttons (both original and compact layouts)
      ['desktopPauseBtn', 'desktopPauseBtnCompact'].forEach(id => {
        const btn = this.shadowRoot.getElementById(id);
        if (!btn) return;
        btn.classList.remove('paused');
        const pauseIcon = btn.querySelector('.desktop-pause-icon');
        const playIcon = btn.querySelector('.desktop-play-icon');
        if (pauseIcon) pauseIcon.style.display = 'block';
        if (playIcon) playIcon.style.display = 'none';
      });

      // Restore mobile pause button
      if (this.isMobile) {
        const mobileBar = document.getElementById('mobile-voice-call-bar-container');
        const mobilePauseBtn = mobileBar?.querySelector('#mobilePauseBtn');
        if (mobilePauseBtn) {
          mobilePauseBtn.classList.remove('paused');
          const mobilePauseIcon = mobilePauseBtn.querySelector('.mobile-pause-icon');
          const mobilePlayIcon = mobilePauseBtn.querySelector('.mobile-play-icon');
          if (mobilePauseIcon) mobilePauseIcon.style.cssText = 'display: block !important; visibility: visible !important; opacity: 1 !important; width: 15px !important; height: 15px !important; margin: 0 !important; padding: 0 !important;';
          if (mobilePlayIcon) mobilePlayIcon.style.cssText = 'display: none !important; visibility: hidden !important; opacity: 0 !important;';
        }
      }

      // Restore timer dots
      this.shadowRoot.querySelectorAll('.ttp-rec-dot').forEach(dot => dot.classList.remove('paused'));
      if (this.isMobile) {
        const mobileBar = document.getElementById('mobile-voice-call-bar-container');
        mobileBar?.querySelectorAll('.mobile-duration-dot').forEach(dot => dot.classList.remove('paused'));
      }

      console.log('▶️ Resuming call...');
    }
  }

  /**
   * Toggle speaker (placeholder)
   */
  toggleSpeaker() {
    if (!this.sdk || !this.isActive) {
      return;
    }
    
    // Toggle speaker mute state
    this.isSpeakerMuted = !this.isSpeakerMuted;
    
    // Control audio playback volume through AudioPlayer
    if (this.sdk.voiceSDK && this.sdk.voiceSDK.audioPlayer) {
      this.sdk.voiceSDK.audioPlayer.setMuted(this.isSpeakerMuted);
      console.log('🔊 Speaker toggle:', this.isSpeakerMuted ? 'muted' : 'unmuted');
    } else {
      console.warn('⚠️ Cannot toggle speaker - audioPlayer not available');
    }
    
    // Update desktop speaker button (both original and compact layouts)
    if (!this.isMobile) {
      const speakerBtn = this.shadowRoot.getElementById('desktopSpeakerBtn');
      const speakerBtnCompact = this.shadowRoot.getElementById('desktopSpeakerBtnCompact');
      
      // Update original layout button
      if (speakerBtn) {
        const speakerIcon = speakerBtn.querySelector('.desktop-speaker-icon');
        const speakerOffIcon = speakerBtn.querySelector('.desktop-speaker-off-icon');
        if (speakerIcon && speakerOffIcon) {
          if (this.isSpeakerMuted) {
            speakerBtn.classList.add('muted');
            speakerIcon.style.display = 'none';
            speakerOffIcon.style.display = 'block';
          } else {
            speakerBtn.classList.remove('muted');
            speakerIcon.style.display = 'block';
            speakerOffIcon.style.display = 'none';
          }
        }
      }
      
      // Update compact layout button (sync state)
      if (speakerBtnCompact) {
        const speakerIconCompact = speakerBtnCompact.querySelector('.desktop-speaker-icon');
        const speakerOffIconCompact = speakerBtnCompact.querySelector('.desktop-speaker-off-icon');
        if (speakerIconCompact && speakerOffIconCompact) {
          if (this.isSpeakerMuted) {
            speakerBtnCompact.classList.add('muted');
            speakerIconCompact.style.display = 'none';
            speakerOffIconCompact.style.display = 'block';
          } else {
            speakerBtnCompact.classList.remove('muted');
            speakerIconCompact.style.display = 'block';
            speakerOffIconCompact.style.display = 'none';
          }
        }
      }
    }
    
    // Update mobile speaker button (container-scoped for Shopify)
    if (this.isMobile) {
      const mobileBar = document.getElementById('mobile-voice-call-bar-container');
      const mobileSpeakerBtn = mobileBar?.querySelector('#mobileSpeakerBtn');
      const mobileSpeakerIcon = mobileSpeakerBtn?.querySelector('.mobile-speaker-icon');
      const mobileSpeakerOffIcon = mobileSpeakerBtn?.querySelector('.mobile-speaker-off-icon');
      
      if (mobileSpeakerBtn && mobileSpeakerIcon && mobileSpeakerOffIcon) {
        if (this.isSpeakerMuted) {
          // Muted: hide white icon, show red icon
          mobileSpeakerBtn.classList.add('muted');
          mobileSpeakerIcon.style.cssText = 'display: none !important; visibility: hidden !important; opacity: 0 !important;';
          mobileSpeakerOffIcon.style.cssText = 'display: block !important; visibility: visible !important; opacity: 1 !important; width: 15px !important; height: 15px !important; margin: 0 !important; padding: 0 !important;';
        } else {
          // Unmuted: show white icon, hide red icon
          mobileSpeakerBtn.classList.remove('muted');
          mobileSpeakerIcon.style.cssText = 'display: block !important; visibility: visible !important; opacity: 1 !important; width: 15px !important; height: 15px !important; margin: 0 !important; padding: 0 !important;';
          mobileSpeakerOffIcon.style.cssText = 'display: none !important; visibility: hidden !important; opacity: 0 !important;';
        }
      }
    }
  }

  /**
   * Update mobile status indicator
   * Delegates to mobile module
   */
  updateMobileStatus(status) {
    return this.mobile.updateMobileStatus(status);
  }

  /**
   * Create mobile minimized bar
   * Delegates to mobile module
   */
  createMobileMinimizedBar() {
    return this.mobile.createMobileMinimizedBar();
  }

  /**
   * Mobile: ensure minimized bar exists with copy for the native getUserMedia permission dialog.
   */
  showMobileCallBarAwaitingBrowserMic() {
    if (!document.getElementById('mobile-voice-call-bar-container')) {
      this.createMobileMinimizedBar();
    }
    const root = document.getElementById('mobile-voice-call-bar-container');
    if (!root) return;
    const line = this.t('awaitingBrowserMic');
    const detail = this.t('awaitingBrowserMicDetail');
    const status = root.querySelector('#mobileStatusText');
    const headerStatus = root.querySelector('#mobileHeaderStatusText');
    if (status) status.textContent = line;
    if (headerStatus) headerStatus.textContent = line;
    const tx = root.querySelector('#mobileTranscriptText');
    if (tx) tx.textContent = detail;
    this.collapseMobileTranscript();
    this.mobile.setTranscriptRowInteractive(false);
  }

  /**
   * Mobile: after mic permission granted, show standard "connecting" copy until audio starts.
   */
  applyMobileBarConnectingCopy() {
    const root = document.getElementById('mobile-voice-call-bar-container');
    if (!root) return;
    const line = this.t('connecting');
    const detail = this.t('connectingSubtitle');
    const status = root.querySelector('#mobileStatusText');
    const headerStatus = root.querySelector('#mobileHeaderStatusText');
    if (status) status.textContent = line;
    if (headerStatus) headerStatus.textContent = line;
    const tx = root.querySelector('#mobileTranscriptText');
    if (tx) tx.textContent = detail;
    this.collapseMobileTranscript();
    this.mobile.setTranscriptRowInteractive(false);
  }

  /**
   * Remove mobile minimized bar
   * Delegates to mobile module
   */
  removeMobileMinimizedBar() {
    return this.mobile.removeMobileMinimizedBar();
  }

  /**
   * Expand mobile transcript
   * Delegates to mobile module
   */
  expandMobileTranscript() {
    return this.mobile.expandMobileTranscript();
  }

  /**
   * Collapse mobile transcript
   * Delegates to mobile module
   */
  collapseMobileTranscript() {
    return this.mobile.collapseMobileTranscript();
  }

  /**
   * Add mobile message
   * Delegates to mobile module
   */
  addMobileMessage(type, text) {
    return this.mobile.addMobileMessage(type, text);
  }

  /**
   * Start mobile waveform animation
   * Delegates to mobile module
   */
  startWaveformAnimation() {
    return this.mobile.startWaveformAnimation();
  }

  /**
   * Start mobile call timer early (when bar is created, before permission granted).
   * Prevents timer/waveform from appearing stuck during 1-3s connecting phase.
   */
  startMobileCallTimerEarly() {
    if (this.callStartTime != null || this.callTimerInterval) return;
    this.callStartTime = Date.now();
    const updateTimer = () => {
      if (!this.callStartTime) return;
      const elapsed = Date.now() - this.callStartTime;
      const minutes = Math.floor(elapsed / 60000);
      const seconds = Math.floor((elapsed % 60000) / 1000);
      const timerText = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
      this._updateTimerElements(timerText, elapsed);
    };
    updateTimer();
    this.callTimerInterval = setInterval(updateTimer, 1000);
  }

  /**
   * Stop mobile waveform animation
   * Delegates to mobile module
   */
  stopWaveformAnimation() {
    return this.mobile.stopWaveformAnimation();
  }

  /**
   * Reset mic and speaker mute state + UI for a fresh call
   */
  resetMuteState() {
    this.isMicMuted = false;
    this.isSpeakerMuted = false;

    console.log('%c🎤 MIC/SPEAKER RESET - New call starting unmuted', 'background: #10b981; color: white; font-size: 14px; font-weight: bold; padding: 4px 8px; border-radius: 4px;');

    // Ensure audio track is enabled
    if (this.sdk?.voiceSDK?.audioRecorder?.mediaStream) {
      const track = this.sdk.voiceSDK.audioRecorder.mediaStream.getAudioTracks()[0];
      if (track) track.enabled = true;
    }

    // Ensure speaker is unmuted
    if (this.sdk?.voiceSDK?.audioPlayer) {
      this.sdk.voiceSDK.audioPlayer.setMuted(false);
    }

    // Reset desktop mute buttons (original + compact)
    ['desktopMuteBtn', 'desktopMuteBtnCompact'].forEach(id => {
      const btn = this.shadowRoot.getElementById(id);
      if (!btn) return;
      btn.classList.remove('muted');
      const micIcon = btn.querySelector('.desktop-mic-icon');
      const micOffIcon = btn.querySelector('.desktop-mic-off-icon');
      if (micIcon) micIcon.style.display = 'block';
      if (micOffIcon) micOffIcon.style.display = 'none';
    });

    // Reset desktop speaker buttons (original + compact)
    ['desktopSpeakerBtn', 'desktopSpeakerBtnCompact'].forEach(id => {
      const btn = this.shadowRoot.getElementById(id);
      if (!btn) return;
      btn.classList.remove('muted');
      const speakerIcon = btn.querySelector('.desktop-speaker-icon');
      const speakerOffIcon = btn.querySelector('.desktop-speaker-off-icon');
      if (speakerIcon) speakerIcon.style.display = 'block';
      if (speakerOffIcon) speakerOffIcon.style.display = 'none';
    });

    // Reset mobile mute/speaker (container-scoped for Shopify)
    const mobileBar = document.getElementById('mobile-voice-call-bar-container');
    const mobileMuteBtn = mobileBar?.querySelector('#mobileMuteBtn');
    if (mobileMuteBtn) {
      mobileMuteBtn.classList.remove('muted');
      const mobileMicIcon = mobileMuteBtn.querySelector('.mobile-mic-icon');
      const mobileMicOffIcon = mobileMuteBtn.querySelector('.mobile-mic-off-icon');
      if (mobileMicIcon) mobileMicIcon.style.cssText = 'display: block !important; visibility: visible !important; opacity: 1 !important; width: 15px !important; height: 15px !important; margin: 0 !important; padding: 0 !important;';
      if (mobileMicOffIcon) mobileMicOffIcon.style.cssText = 'display: none !important; visibility: hidden !important; opacity: 0 !important;';
    }
    const mobileSpeakerBtn = mobileBar?.querySelector('#mobileSpeakerBtn');
    if (mobileSpeakerBtn) {
      mobileSpeakerBtn.classList.remove('muted');
      const mobileSpeakerIcon = mobileSpeakerBtn.querySelector('.mobile-speaker-icon');
      const mobileSpeakerOffIcon = mobileSpeakerBtn.querySelector('.mobile-speaker-off-icon');
      if (mobileSpeakerIcon) mobileSpeakerIcon.style.cssText = 'display: block !important; visibility: visible !important; opacity: 1 !important; width: 15px !important; height: 15px !important; margin: 0 !important; padding: 0 !important;';
      if (mobileSpeakerOffIcon) mobileSpeakerOffIcon.style.cssText = 'display: none !important; visibility: hidden !important; opacity: 0 !important;';
    }
  }

  /**
   * Cleanup resources
   */
  destroy() {
    // End call if active
    if (this.isActive) {
      this.endVoiceCall();
    }
    
    // Clean up voice call resources
    if (this.audioStream) {
      this.audioStream.getTracks().forEach(track => track.stop());
      this.audioStream = null;
    }
    if (this.callTimerInterval) {
      clearInterval(this.callTimerInterval);
      this.callTimerInterval = null;
    }
    
    // Disconnect SDK
    if (this.sdk) {
      this.sdk.disconnect();
      this.sdk = null;
    }
  }
}